import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons";
import { getCreateTransactionBalance } from "../../../../redux/features/owner/ownerAllVerification/ownerAllVerification";
import { GlobalConfiguration } from "../../../../utils/configurationProvider";
import formatTotalBalance from "../../../../utils/formatTotalBalance";
import globalText from "../../../../utils/globalText";
import UniversalRefreshToast from "../../../_utils/UniversalRefreshToast";
import Loader from "../../../Loader/Index";
import useServiceChecker from "../../../ServiceChecker";
import CommonBankLogo from "../../CommonBankLogo";
import IconTearDropButton from "../../CommonButtons/IconTearDropButton";
import PrimaryWithIconButton from "../../CommonButtons/PrimaryWithIconButton";
import CommonToast from "../../CommonToast";
import CardActionableButton from "../../CommonViewAllCard/CardSubComponent/CardActionableButton";
import { formatAccountNumber } from "../../FormatAccountNumber";
import SVGComponent from "../../SVGComponent";
import TickerAnimation from "../../TickerAnimation";

/* =============================================================================
 * CommonDestinationAccount
 *
 * Reusable account / user selection card.
 *
 * variant:
 * - "account" (default): destination bank account card
 * - "user": linked user card (username + user id)
 *
 * Each variant has its own defaults (header, texts, register route, flags).
 * Pass a prop only when you need to override a default.
 *
 * IMPORTANT:
 * This component does NOT own the selection popup. The parent owns:
 * - popup open/close state
 * - selection API
 * - selected account/user handling
 *
 * Read-only mode (isReadOnly) hides select / create / edit / delete actions.
 *
 * BALANCE / REFRESH:
 * - Balance tile + refresh button are shown ONLY when `showsBalance` is true
 *   (parent passes true only for the internal flow).
 * - Refresh button is visible only when the destination owner is active AND
 *   the user has "VAccountBalance" or "Admin" service.
 * ========================================================================== */

/* =============================================================================
 * 1. DEFAULTS
 * ========================================================================== */

const DEFAULT_PAYLOAD_KEYS = Object.freeze({
  accountNumber: "bankAccountNo",
  ifsc: "bankAccountIFSC",
  accountName: "bankAccountName",
  fallbackAccountName: "destinationAccountName",
  ownerId: "destinationAcountOwnerId",
  isOwnerActive: "isDestinationOwnerActive",
  balance: "destinationAccountBalance",
  balanceLastUpdated: "destinationAcountOwnerLastUpdated",
  userName: "userName",
  userId: "userId",
});

const USER_BASE_DEFAULTS = Object.freeze({
  header: "User",
  selectAccountButtonText: "Link user",
  createAccountButtonText: "Create user",
  selectIcon: ICONS?.USERS,
  registerRoute: "/settings/users/create_user",
  isOptional: true,
  requiresSourceAccount: false,
  showsBalance: false,
  supportsAssociatedAccounts: false,
  supportsRemove: false,
});

const USER_DEFAULTS = Object.freeze({
  fieldForce: {
    ...USER_BASE_DEFAULTS,
    subHeader: "Linked user will be able to create requests.",
  },
  reimbursement: {
    ...USER_BASE_DEFAULTS,
    subHeader: "Linked user will be able to create reimbursement requests.",
    supportsRemove: true,
  },
});

const ACCOUNT_DEFAULTS = Object.freeze({
  header: globalText?.DESTINATION_ACCOUNT_CAPITALIZE,
  subHeader: globalText?.DESTINATION_ACCOUNT_DES_TEXT,
  selectAccountButtonText: globalText?.SELECT_DESTINATION_ACCOUNT,
  createAccountButtonText: globalText?.REGISTER_NEW_ACCOUNT,
  selectIcon: ICONS?.SELECT_ACCOUNT,
  registerRoute: null,
  isOptional: false,
  requiresSourceAccount: true,
  showsBalance: false,
  supportsAssociatedAccounts: true,
  supportsRemove: false,
});

/* =============================================================================
 * 2. BALANCE REFRESH HOOK
 * ========================================================================== */

const IDLE_REFRESH_SESSION = {
  isActive: false,
  isCompleted: false,
  hasSeenLoading: false,
  hasShownToast: false,
  toastId: null,
};

/**
 * Handles destination account balance refresh.
 * Same permission + toast logic as SourceDestinationAccountDetail.
 *
 * @param {Object} options
 * @param {boolean} options.enabled - pass `showsBalance` (internal flow only)
 * @param {Function} options.onBalanceFetched
 */
const useBalanceRefresh = ({ enabled, onBalanceFetched }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [ensureAll] = useServiceChecker();

  const services = useSelector((state) => state.refreshToken.services);

  const hasBalanceService =
    ensureAll(["VAccountBalance"]).status || ensureAll(["Admin"]).status;

  const { isLoading, data, error } = useSelector(
    (state) => ({
      isLoading: state.ownerAllVerification.createTransactionBankRefreshLoading,
      data: state.ownerAllVerification.createTransactionBankRefreshData,
      error: state.ownerAllVerification.createTransactionBankRefreshError,
    }),
    shallowEqual,
  );

  const sessionRef = useRef({ ...IDLE_REFRESH_SESSION });

  const [isFetching, setIsFetching] = useState(false);

  const [trend, setTrend] = useState({
    ownerId: null,
    previousBalance: null,
  });

  useEffect(() => {
    const session = sessionRef.current;

    if (!session.isActive) {
      return;
    }

    if (isLoading) {
      session.hasSeenLoading = true;

      if (!session.hasShownToast) {
        session.hasShownToast = true;
        session.toastId = UniversalRefreshToast();
      }

      return;
    }

    if (session.isCompleted || !session.hasSeenLoading) {
      return;
    }

    const finish = (toastOptions) => {
      session.isCompleted = true;

      toast.dismiss(session.toastId);

      CommonToast({
        buttonNeeded: false,
        ...toastOptions,
      });

      sessionRef.current = { ...IDLE_REFRESH_SESSION };
    };

    if (data?.status) {
      finish({
        text: "Balance refreshed successfully.",
        icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
        variant: "var(--success)",
      });
    } else if (error) {
      finish({
        text: error,
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
      });
    }
  }, [isLoading, data, error]);

  const refresh = useCallback(
    async ({ ownerId, previousBalance }) => {
      if (!enabled) {
        return;
      }
      if (
        !(services?.includes("VAccountBalance") || services?.includes("Admin"))
      ) {
        navigate("/feature_not_assigned", {
          state: {
            unavailableService: ["VAccountBalance", "Admin"],
          },
        });
        return;
      }

      if (sessionRef.current.isActive) {
        return;
      }

      sessionRef.current = {
        ...IDLE_REFRESH_SESSION,
        isActive: true,
      };

      setTrend({
        ownerId,
        previousBalance,
      });

      setIsFetching(true);

      try {
        const response = await dispatch(getCreateTransactionBalance(ownerId));

        if (response?.payload?.status === true) {
          onBalanceFetched?.(response.payload?.owner_accounts?.[0]);
        }
      } finally {
        setIsFetching(false);

        if (!sessionRef.current.hasSeenLoading) {
          sessionRef.current = { ...IDLE_REFRESH_SESSION };
        }
      }
    },
    [enabled, services, dispatch, navigate, onBalanceFetched],
  );

  return {
    refresh,
    isFetching,
    isBankRefreshLoading: isLoading,
    trend,
    hasBalanceService,
  };
};

/* =============================================================================
 * 3. SMALL BUILDING BLOCKS
 * ========================================================================== */

const TILE_CLASS = "flex flex-col gap-gap-xs";

const InfoTile = ({ label, children }) => (
  <div className={TILE_CLASS}>
    {children}

    <span className="text-xs text-text-secondary">{label}</span>
  </div>
);

const isRedactedBalance = (value) =>
  value === "xxxx.xx" || value === null || value === undefined || isNaN(+value);

/* =============================================================================
 * 4. BALANCE TILE
 * ========================================================================== */

const BalanceTile = ({
  balance,
  lastUpdated,
  isOwnerActive,
  ownerId,
  trend,
  isFetching,
  isBankRefreshLoading,
  canRefresh,
  onRefresh,
  refreshButtonTitle,
}) => {
  const hasBalance = Boolean(balance);

  const amount = hasBalance
    ? formatTotalBalance(Number(balance), "Default")
    : null;

  const meta = hasBalance ? formatTotalBalance(balance, "Default") : null;

  const balanceColor = !hasBalance
    ? "text-text-primary"
    : Number(balance) < GlobalConfiguration.lengthCheckZero
      ? "text-failed"
      : "text-settled";

  const balanceText = hasBalance
    ? `${isRedactedBalance(balance) ? "" : "INR "}${amount.amount} ${amount.abbr}`
    : globalText?.NOT_AVAILABLE;

  const captionText = isOwnerActive
    ? `${
        meta?.prefix === "crores"
          ? `Balance in ${meta.prefix} - `
          : globalText?.BALANCE_UPDATED
      } ${lastUpdated}`
    : "Balance";

  const showTrend = !isBankRefreshLoading && ownerId === trend.ownerId;

  const isIncrease =
    showTrend && Number(balance) > Number(trend.previousBalance);

  const isDecrease =
    showTrend && Number(balance) < Number(trend.previousBalance);

  return (
    <div className="flex flex-col gap-gap-sm">
      <div className="border-t border-primary-bg"></div>
      <div className="flex flex-col w-width-full h-auto rounded-border-radius-md gap-gap-base justify-between Qlg:flex-row Qlg:gap-gap-xs">
        <div className="w-width-full relative flex justify-between">
          <div className="flex flex-col gap-gap-xs">
            <div className="flex items-center gap-gap-xs relative">
              <div
                className={`flex text-left text-sm font-semibold ${balanceColor}`}
              >
                {balanceText}
              </div>

              {isFetching ? (
                <Loader height="var(--icon-sm)" width="var(--icon-sm)" />
              ) : (
                <div className="relative h-icon-sm w-icon-sm">
                  {isIncrease && (
                    <SVGComponent url={ICONS.ARROW_UP_GREEN_GIF} />
                  )}

                  {isDecrease && (
                    <SVGComponent url={ICONS.ARROW_DOWN_RED_GIF} />
                  )}
                </div>
              )}
            </div>

            <div className="text-left text-xs text-text-secondary whitespace-nowrap">
              <div className="w-full max-w-full overflow-hidden">
                <TickerAnimation text={captionText} />
              </div>
            </div>
          </div>

          {isOwnerActive && canRefresh && (
            <div className="flex flex-col justify-center">
              <CardActionableButton
                btnType="button"
                bgColor="var(--primary-nav-act)"
                disabledBgColor="var(--primary-nav-act)"
                handleFunction={onRefresh}
                icon={ICONS?.RELOAD_REFRESH_WHITE}
                hoverOption="white"
                title={refreshButtonTitle}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* =============================================================================
 * 5. MAIN COMPONENT
 * ========================================================================== */

const CommonDestinationAccount = ({
  variant = "account", // "account" | "user"
  userType = "fieldForce", // "fieldForce" | "reimbursement"
  transactionPayload,
  setTransactionPayload,

  // optional overrides (fall back to the variant preset)
  header,
  subHeader,
  selectAccountButtonText,
  createAccountButtonText,
  registerRoute,
  isOptional,
  requiresSourceAccount,
  showsBalance,
  supportsAssociatedAccounts,
  supportsRemove,
  isSelected,

  // callbacks
  onSelectAccount,
  onEditAccount,
  onDeleteAccount,
  onViewAssociatedAccounts,

  // behaviour flags
  isReadOnly = false,
  isDisabled = false,
  isSelectDisabled = false,
  isEditDisabled = false,
  isCreateDisabled = false,
  canCreateAccount = true,
  showAssociatedAccounts = false,
  isAssociatedAccountsDisabled = false,
  associatedAccountsTitle = "Associated Accounts Found",
  associatedAccountsText,
  refreshButtonTitle = "Refresh",
  editButtonTitle = globalText?.EDIT,
  payloadKeys,
  bankLogoVariant = "big",
}) => {
  const navigate = useNavigate();

  const keys = useMemo(
    () => ({
      ...DEFAULT_PAYLOAD_KEYS,
      ...payloadKeys,
    }),
    [payloadKeys],
  );

  /* ---------- variant + resolved config ---------- */

  const isUser = variant === "user";
  const preset = isUser
    ? (USER_DEFAULTS[userType] ?? USER_DEFAULTS.fieldForce)
    : ACCOUNT_DEFAULTS;
  const cfg = {
    header: header ?? preset.header,
    subHeader: subHeader ?? preset.subHeader,
    selectText: selectAccountButtonText ?? preset.selectAccountButtonText,
    createText: createAccountButtonText ?? preset.createAccountButtonText,
    selectIcon: preset.selectIcon,
    registerRoute: registerRoute ?? preset.registerRoute,
    isOptional: isOptional ?? preset.isOptional,
    requiresSourceAccount:
      requiresSourceAccount ?? preset.requiresSourceAccount,
    showsBalance: showsBalance ?? preset.showsBalance,
    supportsAssociatedAccounts:
      supportsAssociatedAccounts ?? preset.supportsAssociatedAccounts,
    supportsRemove: supportsRemove ?? preset.supportsRemove,
  };

  /* ---------- payload values ---------- */

  const hasSourceAccount = Boolean(transactionPayload?.ownerBankAccountNo);

  const accountNumber = transactionPayload?.[keys.accountNumber];

  const ifsc = transactionPayload?.[keys.ifsc];

  const accountName =
    transactionPayload?.[keys.accountName] ||
    transactionPayload?.[keys.fallbackAccountName];

  const hasAccount =
    isSelected ??
    (isUser
      ? Boolean(transactionPayload?.[keys.userName])
      : Boolean(accountNumber && ifsc));

  /* ---------- visibility + disabled states ---------- */

  const isUserReadOnly = isReadOnly && isUser;

  const showSelectAccount = !isReadOnly;
  const showCreateAccount = !isReadOnly && Boolean(cfg.registerRoute);
  const showEditButton = !isReadOnly && hasAccount;
  const showRemoveButton = showEditButton && isUser && cfg.supportsRemove;

  const shouldDisableDestination =
    isDisabled || (cfg.requiresSourceAccount && !hasSourceAccount);

  const selectDisabled =
    isReadOnly || shouldDisableDestination || isSelectDisabled;

  const editDisabled = isReadOnly || shouldDisableDestination || isEditDisabled;

  const createDisabled =
    isReadOnly ||
    shouldDisableDestination ||
    isCreateDisabled ||
    !canCreateAccount;

  const associatedAccountsDisabled = isReadOnly || isAssociatedAccountsDisabled;

  const canShowAssociated =
    showAssociatedAccounts &&
    cfg.supportsAssociatedAccounts &&
    Boolean(onViewAssociatedAccounts);

  /* ---------- balance ---------- */

  const handleBalanceFetched = useCallback(
    (ownerAccount) => {
      setTransactionPayload?.((prev) => ({
        ...prev,

        [keys.balanceLastUpdated]:
          ownerAccount?.owner_account_balance_last_fetched_at_in_words,

        [keys.balance]: ownerAccount?.owner_account_balance,
      }));
    },
    [setTransactionPayload, keys],
  );

  const {
    refresh,
    isFetching,
    isBankRefreshLoading,
    trend,
    hasBalanceService,
  } = useBalanceRefresh({
    enabled: cfg.showsBalance,
    onBalanceFetched: handleBalanceFetched,
  });

  const handleRefreshClick = useCallback(
    (event) => {
      event.stopPropagation();
      refresh({
        ownerId: transactionPayload?.[keys.ownerId],
        previousBalance: transactionPayload?.[keys.balance],
      });
    },
    [refresh, transactionPayload, keys],
  );

  /* ---------- handlers ---------- */

  const handleSelect = useCallback(() => {
    if (selectDisabled) {
      return;
    }

    onSelectAccount?.();
  }, [selectDisabled, onSelectAccount]);

  const handleEdit = useCallback(() => {
    if (editDisabled) {
      return;
    }

    (onEditAccount ?? onSelectAccount)?.();
  }, [editDisabled, onEditAccount, onSelectAccount]);

  const handleDelete = useCallback(() => {
    if (editDisabled || !cfg.supportsRemove) {
      return;
    }

    setTransactionPayload?.((prev) => ({
      ...prev,
      [keys.userName]: "",
      [keys.userId]: "",
    }));

    onDeleteAccount?.();
  }, [
    editDisabled,
    cfg.supportsRemove,
    setTransactionPayload,
    keys,
    onDeleteAccount,
  ]);

  const handleRegister = useCallback(() => {
    if (createDisabled || !cfg.registerRoute) {
      return;
    }

    navigate(cfg.registerRoute);
  }, [createDisabled, cfg.registerRoute, navigate]);

  const handleAssociatedAccounts = useCallback(() => {
    if (associatedAccountsDisabled) {
      return;
    }

    onViewAssociatedAccounts?.();
  }, [associatedAccountsDisabled, onViewAssociatedAccounts]);

  /* ---------- render ---------- */

  return (
    <div className="w-width-full">
      <div
        className={`flex flex-col relative rounded-border-radius-md ${
          canShowAssociated ? "gap-gap-md" : ""
        }`}
      >
        <div
          className={`bg-primary-nav flex flex-col relative rounded-border-radius-md ${
            shouldDisableDestination
              ? "opacity-light-opacity cursor-not-allowed pointer-events-none"
              : ""
          }`}
        >
          <div className="p-padding-md h-height-full bg-primary-card flex flex-col rounded-border-radius-md">
            {showEditButton && (
              <div className="top-top-base right-right-base absolute flex bg-primary-nav-act rounded-bl-border-radius-md rounded-tr-border-radius-md">
                <IconTearDropButton
                  handleFunction={handleEdit}
                  isDisabled={editDisabled}
                  isLoading={false}
                  enabledBgColor="var(--primary-nav-act)"
                  icon={ICONS?.EDIT}
                  hoverOption="white"
                  title={editButtonTitle}
                  customEdges={
                    showRemoveButton ? "rounded-bl-border-radius-md" : false
                  }
                />
                {showRemoveButton && (
                  <>
                    <div className="border-r border-primary-bg my-margin-button-s"></div>
                    <IconTearDropButton
                      handleFunction={handleDelete}
                      isDisabled={editDisabled}
                      isLoading={false}
                      enabledBgColor="var(--primary-nav-act)"
                      icon={ICONS?.DELETE_WHITE_FILLED}
                      hoverOption="white"
                      title={globalText?.REMOVE}
                      customEdges={"rounded-tr-border-radius-md"}
                    />
                  </>
                )}
              </div>
            )}

            <div className="flex flex-col gap-gap-md">
              <div className="flex flex-col gap-gap-xs">
                <span className="text-font-md text-text-primary font-semibold">
                  {cfg.header}
                  {cfg.isOptional && (
                    <span className="font-normal"> (Optional)</span>
                  )}
                </span>

                {!isUserReadOnly && cfg.subHeader && (
                  <span className="text-xs text-text-secondary">
                    {cfg.subHeader}
                  </span>
                )}
              </div>

              {isUserReadOnly && !hasAccount && (
                <div className="flex flex-col gap-gap-xs">
                  <div className="font-normal text-text-primary text-font-md">
                    {transactionPayload?.[keys.userName] || "Not linked"}
                  </div>
                  <div className="text-font-xs font-normal text-text-secondary">
                    Username
                  </div>
                </div>
              )}

              {!hasAccount ? (
                (showSelectAccount || showCreateAccount) && (
                  <div>
                    {showSelectAccount && (
                      <PrimaryWithIconButton
                        btnText={cfg.selectText}
                        enabledBgColor="var(--primary-nav-act)"
                        enabledTextColor="var(--text-primary)"
                        isDisabled={selectDisabled}
                        handleFunction={handleSelect}
                        iconToBeAlign="left"
                        icon={cfg.selectIcon}
                        fullWidthRequired
                        hoverOption="white"
                        customPaddingRequired={"py-padding-sm"}
                      />
                    )}

                    {showCreateAccount && (
                      <div className="mt-margin-md">
                        <PrimaryWithIconButton
                          btnText={cfg.createText}
                          enabledBgColor="var(--primary-nav-act)"
                          enabledTextColor="var(--text-primary)"
                          imgColor="var(--text-primary)"
                          isDisabled={createDisabled}
                          handleFunction={handleRegister}
                          iconToBeAlign="left"
                          icon={ICONS?.ADD_BUTTON_WHITE}
                          fullWidthRequired
                          hoverOption="white"
                          customPaddingRequired={"py-padding-sm"}
                        />
                      </div>
                    )}
                  </div>
                )
              ) : (
                <div className="flex flex-col gap-gap-sm">
                  <div className="flex items-center justify-between gap-gap-xs">
                    <InfoTile
                      label={isUser ? "Username" : globalText?.ACCOUNT_NUMBER}
                    >
                      <span
                        className={`text-text-primary ${
                          isUser
                            ? "font-normal text-font-md"
                            : "text-sm font-semibold"
                        }`}
                      >
                        {isUser
                          ? transactionPayload?.[keys.userName]
                          : formatAccountNumber(accountNumber)}
                      </span>
                    </InfoTile>

                    {!isUser && (
                      <CommonBankLogo
                        ifsc={ifsc}
                        variant={bankLogoVariant}
                        type="pill"
                      />
                    )}
                  </div>

                  <div className="border-t border-primary-bg"></div>

                  <InfoTile
                    label={isUser ? "User ID" : globalText?.ACCOUNT_NAME}
                  >
                    <div
                      className={`text-text-primary ${
                        isUser
                          ? "font-normal text-font-md"
                          : "text-sm font-semibold"
                      }`}
                    >
                      <div className="flex-1 truncate flex">
                        <TickerAnimation
                          text={
                            isUser
                              ? transactionPayload?.[keys.userId]
                              : accountName
                          }
                        />
                      </div>
                    </div>
                  </InfoTile>

                  {cfg.showsBalance && (
                    <BalanceTile
                      balance={transactionPayload?.[keys.balance]}
                      lastUpdated={
                        transactionPayload?.[keys.balanceLastUpdated]
                      }
                      isOwnerActive={transactionPayload?.[keys.isOwnerActive]}
                      ownerId={transactionPayload?.[keys.ownerId]}
                      trend={trend}
                      isFetching={isFetching}
                      isBankRefreshLoading={isBankRefreshLoading}
                      canRefresh={hasBalanceService}
                      onRefresh={handleRefreshClick}
                      refreshButtonTitle={refreshButtonTitle}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {canShowAssociated && (
          <div
            role="button"
            tabIndex={associatedAccountsDisabled ? -1 : 0}
            onClick={
              associatedAccountsDisabled ? undefined : handleAssociatedAccounts
            }
            onKeyDown={(e) => {
              if (
                !associatedAccountsDisabled &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                handleAssociatedAccounts();
              }
            }}
            className={`w-width-full flex items-start justify-between gap-gap-md bg-primary-card rounded-border-radius-md p-padding-md relative z-z-index-base ${
              associatedAccountsDisabled
                ? "opacity-light-opacity pointer-events-none"
                : "cursor-pointer global-hover-white"
            }`}
          >
            <div className="flex flex-col gap-gap-sm flex-1 min-w-0">
              <p className="text-text-primary font-semibold text-font-md">
                {associatedAccountsTitle}
              </p>

              <ul className="list-disc pl-padding-md text-text-secondary text-font-xs flex flex-col gap-gap-sm">
                {[]
                  .concat(associatedAccountsText)
                  .filter(Boolean)
                  .map((point, i) => (
                    <li key={i}>{point}</li>
                  ))}
              </ul>
            </div>
            <div className="flex-shrink-0 pt-padding-xs">
              <SVGComponent
                url={ICONS?.ARROW_RIGHT_WHITE}
                color="var(--text-primary)"
                height="var(--icon-sm)"
                width="var(--icon-sm)"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommonDestinationAccount;
